import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";
import PaymentReceiptModal from "../components/PaymentReceiptModal";
import EditLeadModal from "../components/EditLeadModal";
import AddFollowupModal from "../components/AddFollowupModal";
import { formatDateTime } from "../utils/dateUtils";

const initialFormState = {};

export default function TeamDashboardPage() {
  const { http, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [forms, setForms] = useState(initialFormState);
  const [savingId, setSavingId] = useState(null);

  const showPaymentInfo = user?.role !== "team_member";

  // View & Modals state
  const [viewMode, setViewMode] = useState("table"); // "table" | "cards"
  const [scheduleFilter, setScheduleFilter] = useState("ALL"); // "ALL" | "TODAY" | "OVERDUE"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedForFollowup, setSelectedForFollowup] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [selectedForPayment, setSelectedForPayment] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get("/api/team/dashboard");
      setItems(res.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load team dashboard.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredItems = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];

    return items.filter((item) => {
      const itemDateStr = item.nextFollowupAt || item.nextAppointmentAt
        ? new Date(item.nextFollowupAt || item.nextAppointmentAt).toISOString().split("T")[0]
        : null;

      if (scheduleFilter === "TODAY") {
        if (!itemDateStr || itemDateStr !== todayStr) return false;
      } else if (scheduleFilter === "OVERDUE") {
        if (!itemDateStr || itemDateStr >= todayStr) return false;
      }

      if (!searchTerm.trim()) return true;

      const query = searchTerm.trim().toLowerCase();
      const appNo = String(item.applicationNo || "").toLowerCase();
      const name = String(item.customerName || "").toLowerCase();
      const mobile = String(item.customerMobile || "").toLowerCase();
      const workType = String(item.latestWorkTypeName || "").toLowerCase();
      const workDesc = String(item.latestWorkDescription || item.latestWorkName || "").toLowerCase();
      const vehicle = String(item.vehicleNo || "").toLowerCase();
      const creator = String(item.createdByName || "").toLowerCase();
      const assigned = String(item.assignedToName || "").toLowerCase();

      return (
        appNo.includes(query) ||
        name.includes(query) ||
        mobile.includes(query) ||
        workType.includes(query) ||
        workDesc.includes(query) ||
        vehicle.includes(query) ||
        creator.includes(query) ||
        assigned.includes(query)
      );
    });
  }, [items, searchTerm, scheduleFilter]);

  const onChange = (enquiryId, name, value) => {
    setForms((prev) => ({
      ...prev,
      [enquiryId]: {
        ...prev[enquiryId],
        [name]: value,
      },
    }));
  };

  const saveFollowup = async (enquiryId) => {
    const form = forms[enquiryId] || {};
    if (!form.progressNote?.trim()) return;
    setSavingId(enquiryId);
    setError(null);
    try {
      const payload = new FormData();
      payload.append("progressNote", form.progressNote);
      if (form.nextFollowupAt) payload.append("nextFollowupAt", form.nextFollowupAt);
      if (form.status) payload.append("status", form.status);
      const pendingFiles = form.pendingFiles || [];
      pendingFiles.forEach((pf) => payload.append("documents", pf.file));
      await http.post(`/api/enquiries/${enquiryId}/followups`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForms((prev) => ({
        ...prev,
        [enquiryId]: { progressNote: "", nextFollowupAt: "", status: "", pendingFiles: [] },
      }));
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save followup.");
    } finally {
      setSavingId(null);
    }
  };

  const openWhatsApp = (item) => {
    const mobileClean = String(item.customerMobile || "").replace(/\D/g, "");
    const formattedPhone = mobileClean.length === 10 ? `91${mobileClean}` : mobileClean;
    const msg = `Hello ${item.customerName}, regarding your RTO Application ${item.applicationNo} (${item.latestWorkTypeName || "RTO Work"}). Please let us know if you have any questions or document updates. Thank you!`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Team Dashboard & Followups 📋</h4>
          <div className="text-muted small">
            Manage lead follow-ups, chat on WhatsApp, edit lead details, and collect payment receipts.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className={`btn ${viewMode === "table" ? "btn-primary fw-bold" : "btn-outline-primary"}`}
              onClick={() => setViewMode("table")}
            >
              📑 Table List
            </button>
            <button
              type="button"
              className={`btn ${viewMode === "cards" ? "btn-primary fw-bold" : "btn-outline-primary"}`}
              onClick={() => setViewMode("cards")}
            >
              🗂️ Card View
            </button>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="card shadow-sm">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-5 col-lg-6">
              <SpeechInput
                as="input"
                name="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search followups by Application No, Customer, Mobile, Work, Vehicle..."
              />
            </div>
            <div className="col-12 col-md-7 col-lg-6 d-flex align-items-center justify-content-md-end gap-2 flex-wrap">
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${scheduleFilter === "ALL" ? "btn-dark fw-bold" : "btn-outline-dark"}`}
                  onClick={() => setScheduleFilter("ALL")}
                >
                  All Leads ({items.length})
                </button>
                <button
                  type="button"
                  className={`btn ${scheduleFilter === "TODAY" ? "btn-warning fw-bold" : "btn-outline-warning text-dark"}`}
                  onClick={() => setScheduleFilter("TODAY")}
                >
                  📅 Today's Followups
                </button>
                <button
                  type="button"
                  className={`btn ${scheduleFilter === "OVERDUE" ? "btn-danger fw-bold" : "btn-outline-danger"}`}
                  onClick={() => setScheduleFilter("OVERDUE")}
                >
                  ⏰ Overdue
                </button>
              </div>
              <span className="text-muted small">
                Showing {filteredItems.length} items
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE VIEW (DEFAULT) */}
      {viewMode === "table" ? (
        <div className="card shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Application No</th>
                    <th>Customer Name</th>
                    <th>Mobile</th>
                    <th>Work Details</th>
                    <th>Next Followup Date</th>
                    <th>Followup Note</th>
                    <th>Lead Taken By</th>
                    <th>Assigned To</th>
                    {showPaymentInfo ? <th>Fee / Balance</th> : null}
                    <th>Status</th>
                    <th className="text-center" style={{ minWidth: "260px" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={showPaymentInfo ? 11 : 10} className="text-center py-4 text-muted">
                        Loading followup leads...
                      </td>
                    </tr>
                  ) : null}

                  {!loading && filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={showPaymentInfo ? 11 : 10} className="text-center py-4 text-muted">
                        No active followup leads found matching criteria.
                      </td>
                    </tr>
                  ) : null}

                  {!loading &&
                    filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-bold">{item.applicationNo}</div>
                        </td>
                        <td className="fw-bold text-uppercase">{item.customerName}</td>
                        <td>
                          <a href={`tel:${item.customerMobile}`} className="text-decoration-none">
                            📱 {item.customerMobile}
                          </a>
                        </td>
                        <td>
                          <div className="fw-semibold small">
                            {item.latestWorkTypeName || "-"}
                          </div>
                          <div className="text-muted small">
                            {item.latestWorkDescription || item.latestWorkName || "-"}
                          </div>
                          {item.vehicleNo ? (
                            <div className="badge text-bg-light border text-dark mt-1">
                              🚗 {item.vehicleNo}
                            </div>
                          ) : null}
                          {item.rtoApplicationNo ? (
                            <div className="badge text-bg-info text-dark border mt-1 ms-1">
                              📋 RTO: {item.rtoApplicationNo}
                            </div>
                          ) : null}
                          {item.licenseType ? (
                            <div className="badge text-bg-light border text-dark mt-1 ms-1">
                              🪪 {item.licenseType}
                            </div>
                          ) : null}
                        </td>
                        <td className="small">
                          {item.nextFollowupAt || item.nextAppointmentAt || item.followups?.[0]?.nextFollowupAt ? (
                            <span className="fw-semibold text-primary">
                              📅 {formatDateTime(item.nextFollowupAt || item.nextAppointmentAt || item.followups[0]?.nextFollowupAt)}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="small">
                          {item.lastProgressNote || item.followups?.[0]?.progressNote ? (
                            <div
                              className="small text-wrap text-break"
                              style={{ maxWidth: "220px", maxHeight: "80px", overflowY: "auto" }}
                            >
                              📝 {item.lastProgressNote || item.followups[0]?.progressNote}
                            </div>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td>
                          <span className="badge text-bg-light text-primary border">
                            {item.createdByName || "Customer"}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold">
                            {item.assignedToName || "Unassigned"}
                          </span>
                        </td>
                        {showPaymentInfo ? (
                          <td>
                            <div className="small">
                              Fee: ₹{Number(item.latestPaymentAmount || 0).toFixed(2)}
                            </div>
                            <div
                              className={`fw-bold small ${
                                Number(item.pendingBalance) > 0 ? "text-danger" : "text-success"
                              }`}
                            >
                              Bal: ₹{Number(item.pendingBalance || 0).toFixed(2)}
                            </div>
                          </td>
                        ) : null}
                        <td>
                          <span className="badge text-bg-warning">{item.status}</span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-primary"
                              title="Add Work Progress Followup"
                              onClick={() => setSelectedForFollowup(item)}
                            >
                              📝 Followup
                            </button>
                            <button
                              type="button"
                              className="btn btn-success"
                              title="Open WhatsApp Chat"
                              onClick={() => openWhatsApp(item)}
                            >
                              💬 WhatsApp
                            </button>
                            <button
                              type="button"
                              className="btn btn-warning text-dark"
                              title="Edit Lead Details"
                              onClick={() => setSelectedForEdit(item)}
                            >
                              ✏️ Edit
                            </button>
                            {showPaymentInfo ? (
                              <button
                                type="button"
                                className="btn btn-outline-success"
                                title="Payments & Receipts"
                                onClick={() => setSelectedForPayment(item)}
                              >
                                💳 Payment
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* CARD VIEW */}
      {viewMode === "cards" ? (
        <div className="row g-3">
          {loading ? <div className="text-muted">Loading...</div> : null}
          {!loading && filteredItems.length === 0 ? (
            <div className="col-12">
              <div className="alert alert-info mb-0">No active followup leads found.</div>
            </div>
          ) : null}

          {!loading &&
            filteredItems.map((item) => {
              const form = forms[item.id] || {
                progressNote: "",
                nextFollowupAt: "",
                pendingFiles: [],
              };

              return (
                <div className="col-12" key={item.id}>
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12 col-lg-5">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold">{item.applicationNo}</div>
                              <div className="text-uppercase fw-bold">{item.customerName}</div>
                              <div className="text-muted small">{item.customerMobile}</div>
                            </div>
                            <span className="badge text-bg-warning">{item.status}</span>
                          </div>
                          <hr />
                          <div className="small d-grid gap-2">
                            <div>
                              <span className="text-muted">Lead Taken By:</span>{" "}
                              <span className="fw-semibold text-primary">
                                {item.createdByName || "Customer Submission"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Assigned To:</span>{" "}
                              <span className="fw-semibold">
                                {item.assignedToName || "Unassigned"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Work Type:</span>{" "}
                              <span className="fw-semibold">{item.latestWorkTypeName || "-"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Work:</span>{" "}
                              {item.latestWorkDescription || item.latestWorkName || "-"}
                            </div>
                            {item.vehicleNo ? (
                              <div>
                                <span className="text-muted">Vehicle No:</span>{" "}
                                <span className="fw-semibold">{item.vehicleNo}</span>
                              </div>
                            ) : null}
                            {item.rtoApplicationNo ? (
                              <div>
                                <span className="text-muted">RTO Application No:</span>{" "}
                                <span className="fw-semibold text-primary">{item.rtoApplicationNo}</span>
                              </div>
                            ) : null}
                            {item.licenseType ? (
                              <div>
                                <span className="text-muted">License Type:</span>{" "}
                                <span className="fw-semibold">{item.licenseType}</span>
                              </div>
                            ) : null}
                            <div>
                              <span className="text-muted">Next Followup Date:</span>{" "}
                              <span className="fw-semibold text-primary">
                                {item.nextFollowupAt || item.nextAppointmentAt || item.followups?.[0]?.nextFollowupAt
                                  ? `📅 ${formatDateTime(item.nextFollowupAt || item.nextAppointmentAt || item.followups[0]?.nextFollowupAt)}`
                                  : "Not set"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted">Latest Followup Note:</span>{" "}
                              <span className="fw-semibold text-dark">
                                {item.lastProgressNote || item.followups?.[0]?.progressNote
                                  ? `📝 ${item.lastProgressNote || item.followups[0]?.progressNote}`
                                  : "No notes"}
                              </span>
                            </div>
                            {showPaymentInfo ? (
                              <>
                                <div>
                                  <span className="text-muted">Total Fee:</span>{" "}
                                  <span className="fw-semibold">
                                    ₹{Number(item.latestPaymentAmount || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted">Total Paid:</span>{" "}
                                  <span className="fw-semibold text-success">
                                    ₹{Number(item.totalPaid || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted">Balance Amount:</span>{" "}
                                  <span
                                    className={`fw-bold ${
                                      Number(item.pendingBalance) > 0 ? "text-danger" : "text-success"
                                    }`}
                                  >
                                    ₹{Number(item.pendingBalance || 0).toFixed(2)}
                                  </span>
                                </div>
                              </>
                            ) : null}

                            <div className="btn-group btn-group-sm mt-2">
                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={() => openWhatsApp(item)}
                              >
                                💬 WhatsApp
                              </button>
                              <button
                                type="button"
                                className="btn btn-warning text-dark"
                                onClick={() => setSelectedForEdit(item)}
                              >
                                ✏️ Edit
                              </button>
                              {showPaymentInfo ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-success"
                                  onClick={() => setSelectedForPayment(item)}
                                >
                                  💳 Payments
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-lg-7">
                          <h6>Add Work Progress</h6>
                          <div className="row g-3">
                            <div className="col-12">
                              <label className="form-label">Progress Note</label>
                              <SpeechInput
                                as="textarea"
                                name={`progressNote_${item.id}`}
                                value={form.progressNote || ""}
                                onChange={(e) =>
                                  onChange(item.id, "progressNote", e.target.value)
                                }
                                rows={3}
                                placeholder="Enter work progress details"
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <label className="form-label">Update Status</label>
                              <select
                                className="form-select"
                                value={form.status || ""}
                                onChange={(e) => onChange(item.id, "status", e.target.value)}
                              >
                                <option value="">Keep current status ({item.status})</option>
                                <option value="IN_PROGRESS">IN_PROGRESS</option>
                                <option value="COMPLETED">COMPLETED</option>
                                <option value="CLOSED">CLOSED</option>
                              </select>
                            </div>
                            <div className="col-12 col-md-6">
                              <label className="form-label">Next Followup Date</label>
                              <input
                                className="form-control"
                                type="datetime-local"
                                value={form.nextFollowupAt || ""}
                                onChange={(e) =>
                                  onChange(item.id, "nextFollowupAt", e.target.value)
                                }
                              />
                            </div>

                            <div className="col-12 d-flex justify-content-end">
                              <button
                                className="btn btn-primary"
                                onClick={() => saveFollowup(item.id)}
                                disabled={savingId === item.id || !form.progressNote?.trim()}
                              >
                                {savingId === item.id ? "Saving..." : "Save Progress"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}

      {/* MODALS */}
      {selectedForFollowup ? (
        <AddFollowupModal
          enquiry={selectedForFollowup}
          onClose={() => setSelectedForFollowup(null)}
          onSaved={loadData}
        />
      ) : null}

      {selectedForEdit ? (
        <EditLeadModal
          enquiry={selectedForEdit}
          onClose={() => setSelectedForEdit(null)}
          onSaved={loadData}
        />
      ) : null}

      {selectedForPayment ? (
        <PaymentReceiptModal
          enquiry={selectedForPayment}
          onClose={() => setSelectedForPayment(null)}
          onPaymentRecorded={loadData}
        />
      ) : null}
    </div>
  );
}
